import { useMemo, useState, useEffect, useRef } from "react";
import { useSearch } from "wouter";
import {
  useGetQuotes,
  getGetQuotesQueryKey,
  type QuoteWithTags,
} from "@workspace/api-client-react";
import { QuoteCard } from "@/components/QuoteCard";

type CardQuote = ReturnType<typeof toCardQuote>;

function toCardQuote(q: QuoteWithTags) {
  return {
    id: q.id,
    text: q.text,
    author: q.author,
    era: q.era,
    year: q.year != null ? String(q.year) : q.era,
    tags: q.tags.map((t) => t.name),
  };
}

const PAGE_SIZE = 40;

export function SearchPage({ searchQuery }: { searchQuery: string }) {
  const search = useSearch();
  const urlQ = new URLSearchParams(search).get("q") ?? "";
  const q = searchQuery || urlQ;

  const [page, setPage] = useState(1);
  const [allQuotes, setAllQuotes] = useState<CardQuote[]>([]);
  // Track the query we last reset for — allows synchronous reset on query change.
  const [trackedQ, setTrackedQ] = useState(q);

  // Synchronous reset: runs during render (before paint) when query changes.
  // This avoids a stale intermediate frame and prevents a wasted fetch with
  // the old page number.
  if (trackedQ !== q) {
    setTrackedQ(q);
    setPage(1);
    setAllQuotes([]);
  }

  const queryParams = {
    page,
    pageSize: PAGE_SIZE,
    status: "approved" as const,
    query: q,
  };

  const { data, isLoading, isFetching } = useGetQuotes(queryParams, {
    query: {
      queryKey: getGetQuotesQueryKey(queryParams),
      enabled: q.trim().length > 0,
    },
  });

  // Append newly fetched page to accumulated list
  const prevData = useRef<typeof data>(undefined);
  useEffect(() => {
    if (!data || data === prevData.current) return;
    prevData.current = data;
    const newQuotes = data.data.map(toCardQuote);
    if (data.page === 1) {
      setAllQuotes(newQuotes);
    } else {
      setAllQuotes((prev) => [...prev, ...newQuotes]);
    }
  }, [data]);

  const total = data?.total ?? 0;
  const hasMore = allQuotes.length < total;

  const quotes = useMemo(() => allQuotes, [allQuotes]);

  if (!q.trim()) {
    return (
      <section data-testid="page-search">
        <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
          <p className="text-sm text-stone-500">Type something above to search quotes and authors.</p>
        </div>
      </section>
    );
  }

  if (isLoading && page === 1) {
    return (
      <section data-testid="page-search">
        <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
          <p className="animate-pulse text-sm text-stone-400">Searching…</p>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="page-search">
      <p className="mb-4 text-sm text-stone-500">
        {quotes.length === 0 ? (
          <>No results for <span className="font-medium text-stone-700">"{q}"</span>.</>
        ) : (
          <>
            Showing{" "}
            <span className="font-medium text-stone-700">{quotes.length}</span>
            {total > quotes.length ? (
              <> of <span className="font-medium text-stone-700">{total}</span></>
            ) : null}{" "}
            result{total !== 1 ? "s" : ""} for{" "}
            <span className="font-medium text-stone-700">"{q}"</span>
          </>
        )}
      </p>
      {quotes.length > 0 && (
        <>
          <div className="grid gap-5 md:grid-cols-2">
            {quotes.map((quote) => (
              <QuoteCard key={quote.id} quote={quote} />
            ))}
          </div>
          {hasMore && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={isFetching}
                className="rounded-2xl border border-stone-200 bg-white px-7 py-2.5 text-sm font-medium text-stone-700 shadow-sm transition hover:bg-stone-50 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 disabled:opacity-50"
              >
                {isFetching ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
